import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminSingleAssetHL.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

export default function AdminSingleAssetHL() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);

  const [paperMode, setPaperMode] = useState(true);
  const [defaultDeployUsd, setDefaultDeployUsd] = useState('5000');
  const [commissionEnabled, setCommissionEnabled] = useState(false);
  const [commissionWallet, setCommissionWallet] = useState('');
  const [commissionBpsBuy, setCommissionBpsBuy] = useState('0');
  const [commissionBpsSell, setCommissionBpsSell] = useState('0');
  const [commissionMinUsd, setCommissionMinUsd] = useState('0.01');
  const [commissionBlockWhenOwed, setCommissionBlockWhenOwed] = useState(false);
  const [commissionOwedBlockUsd, setCommissionOwedBlockUsd] = useState('1');
  const [orderbookPollMs, setOrderbookPollMs] = useState('35000');

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/single-asset-hl/admin/settings`, {
        headers: authHeaders(),
      });
      setPaperMode(data.paperMode !== false);
      setDefaultDeployUsd(String(data.defaultDeployUsd ?? 5000));
      const c = data.commission || {};
      setCommissionEnabled(!!c.enabled);
      setCommissionWallet(c.walletAddress || '');
      setCommissionBpsBuy(String(c.bpsBuy ?? 0));
      setCommissionBpsSell(String(c.bpsSell ?? 0));
      setCommissionMinUsd(String(c.minUsd ?? 0.01));
      setCommissionBlockWhenOwed(!!c.blockWhenOwed);
      setCommissionOwedBlockUsd(String(c.owedBlockUsd ?? 1));
      setOrderbookPollMs(String(data.orderbookPollMs ?? 35000));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaveMsg(null);
    try {
      const { data } = await axios.patch(
        `${API_BASE}/single-asset-hl/admin/settings`,
        {
          paperMode,
          defaultDeployUsd: parseFloat(defaultDeployUsd) || 5000,
          commissionEnabled,
          commissionWalletAddress: commissionWallet.trim(),
          commissionBpsBuy: parseFloat(commissionBpsBuy) || 0,
          commissionBpsSell: parseFloat(commissionBpsSell) || 0,
          commissionMinUsd: parseFloat(commissionMinUsd) || 0.01,
          commissionBlockWhenOwed,
          commissionOwedBlockUsd: parseFloat(commissionOwedBlockUsd) || 1,
          orderbookPollMs: parseInt(orderbookPollMs, 10) || 35000,
        },
        { headers: authHeaders() }
      );
      setSaveMsg(data.message || 'Saved.');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="loading">Loading Single Asset HL settings…</div>;

  return (
    <div className="admin-single-asset-hl">
      <h2>📈 Single Asset — HL</h2>
      <p className="sahl-admin-note">
        Commission for <strong>Single Asset Trading — Hyperliquid</strong> only. Separate from Multi-asset oBot
        commission. Fee = <strong>notional × BPS ÷ 10,000</strong> where notional = margin × leverage (e.g. $10K
        margin × 5x = $50K notional).
      </p>

      {error && <div className="error-message">{error}</div>}
      {saveMsg && <div className="sahl-admin-success">{saveMsg}</div>}

      <form onSubmit={save} className="sahl-admin-form">
        <section className="sahl-admin-card">
          <h3>Trading mode</h3>
          <label className="sahl-checkbox">
            <input type="checkbox" checked={paperMode} onChange={(e) => setPaperMode(e.target.checked)} />
            Paper mode (simulated fills at mid)
          </label>
          <div className="form-group">
            <label>Default deploy (USD margin)</label>
            <input
              type="number"
              min={100}
              step={100}
              value={defaultDeployUsd}
              onChange={(e) => setDefaultDeployUsd(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Order book refresh (seconds)</label>
            <input
              type="number"
              min={10}
              max={120}
              step={1}
              value={Math.round(Number(orderbookPollMs) / 1000) || 35}
              onChange={(e) => setOrderbookPollMs(String((parseInt(e.target.value, 10) || 35) * 1000))}
            />
            <span className="field-hint">
              How often the Single Asset order book polls Hyperliquid (10–120s).{' '}
              <strong>15 seconds is fine</strong> for a small number of traders; use 30–60s if you see
              rate-limit warnings. Current: every {Math.round(Number(orderbookPollMs) / 1000) || 35}s.
            </span>
          </div>
        </section>

        <section className="sahl-admin-card sahl-commission-card">
          <h3>💰 Platform commission (basis points)</h3>
          <p className="sahl-card-desc">
            Charged on each filled trade. Live fills send USDC from the user&apos;s withdrawable HL balance
            immediately after fill. Trades are blocked upfront if free USDC cannot cover margin + HL fee +
            commission + owed balance.
          </p>
          <label className="sahl-checkbox">
            <input
              type="checkbox"
              checked={commissionEnabled}
              onChange={(e) => setCommissionEnabled(e.target.checked)}
            />
            Enable commission collection
          </label>
          <div className="form-group">
            <label>Commission wallet (0x… receives USDC)</label>
            <input
              type="text"
              value={commissionWallet}
              onChange={(e) => setCommissionWallet(e.target.value)}
              placeholder="0x…"
              autoComplete="off"
            />
          </div>
          <div className="form-row-two">
            <div className="form-group">
              <label>Buy BPS</label>
              <input
                type="number"
                min={0}
                max={500}
                step={0.1}
                value={commissionBpsBuy}
                onChange={(e) => setCommissionBpsBuy(e.target.value)}
              />
              <span className="field-hint">Open long / close short</span>
            </div>
            <div className="form-group">
              <label>Sell BPS</label>
              <input
                type="number"
                min={0}
                max={500}
                step={0.1}
                value={commissionBpsSell}
                onChange={(e) => setCommissionBpsSell(e.target.value)}
              />
              <span className="field-hint">Open short / close long</span>
            </div>
          </div>
          <div className="form-group">
            <label>Minimum commission (USD)</label>
            <input
              type="number"
              min={0.01}
              step={0.01}
              value={commissionMinUsd}
              onChange={(e) => setCommissionMinUsd(e.target.value)}
            />
          </div>
        </section>

        <section className="sahl-admin-card sahl-owed-card">
          <h3>⏸️ Pause live trading when commission is owed</h3>
          <label className="sahl-checkbox">
            <input
              type="checkbox"
              checked={commissionBlockWhenOwed}
              onChange={(e) => setCommissionBlockWhenOwed(e.target.checked)}
            />
            Pause new live trades when user owes commission
          </label>
          <div className="form-group">
            <label>Block threshold (USD owed)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={commissionOwedBlockUsd}
              onChange={(e) => setCommissionOwedBlockUsd(e.target.value)}
              disabled={!commissionBlockWhenOwed}
            />
          </div>
        </section>

        <button type="submit" className="primary-btn" disabled={busy}>
          Save Single Asset HL settings
        </button>
      </form>
    </div>
  );
}
